/**
 * The interface language (Phase 7, Lane I): English or German.
 *
 * Two ways a screen gets its words, both reading the one current locale:
 *
 * - Named tables — `L`, `WEEK_LABELS`, a feature's `T` — are made with `localized()`:
 *   an English and a German table of the same shape behind one export, so code keeps
 *   writing `L.home`. The type of the German table is derived from the English one, so
 *   a key missing on either side is a type error.
 * - Everything else is written in English where it is used and wrapped in `tx()`:
 *   `tx('Find a sheet')`, `tx('Added {n} to {sheet}', { n, sheet })`. The German words
 *   live in `./de.ts`, keyed by the English text, and are loaded only when German is
 *   chosen. A key without a German entry shows in English (the unit test makes sure
 *   there is none, and that the catalog holds nothing unused).
 *
 * Read the words while rendering, never at module level: a page imported before the
 * language changed would keep the old ones. A language change re-mounts the routed
 * pages (see LocaleProvider), so every screen reads the new words from the top.
 *
 * Course content — sheets, exercises, their titles — is never translated.
 */

import { setTaxonomyTranslation } from '@studio/shared';

export type Locale = 'en' | 'de';
export const LOCALES: readonly Locale[] = ['en', 'de'];

/** The name of each language, in that language: what the language select lists. */
export const LOCALE_NAMES: Record<Locale, string> = { en: 'English', de: 'Deutsch' };

let current: Locale = 'en';
let catalog: Record<string, string> | null = null;
/** the tags' German names (topics, types, goals), loaded with the catalog */
let taxonomy: Record<string, string> | null = null;

/** The language the interface is in now. */
export const getLocale = (): Locale => current;

/** The browser's language, as one of ours: German for any `de*`, English otherwise. */
export function browserLocale(): Locale {
  try {
    const langs = typeof navigator === 'undefined' ? [] : navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const l of langs) {
      const code = String(l).toLowerCase();
      if (code.startsWith('de')) return 'de';
      if (code.startsWith('en')) return 'en';
    }
  } catch {
    /* no navigator: English */
  }
  return 'en';
}

/** Load what a language needs (the German catalog is its own chunk). */
export async function loadLocale(locale: Locale): Promise<void> {
  if (locale !== 'de' || (catalog && taxonomy)) return;
  const [de, tags] = await Promise.all([import('./de'), import('./taxonomy-de')]);
  catalog = de.DE;
  taxonomy = tags.TAXONOMY_DE;
}

/**
 * Switch the language. Call `loadLocale` first; the words switch at once, and what is
 * already on screen changes when it next renders.
 */
export function setLocale(locale: Locale): void {
  current = locale;
  const tags = locale === 'de' ? taxonomy : null;
  setTaxonomyTranslation(tags ? (key) => tags[key] : null);
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
}

/** For tests: switch with a given catalog, without the dynamic import. */
export function setLocaleForTest(locale: Locale, de?: Record<string, string>, tags?: Record<string, string>): void {
  if (de) catalog = de;
  if (tags) taxonomy = tags;
  setLocale(locale);
}

/* ------------------------------------------------------------------ strings */

const fill = (text: string, vars?: Record<string, string | number>): string =>
  vars ? text.replace(/\{(\w+)\}/g, (all, name: string) => (name in vars ? String(vars[name]) : all)) : text;

/**
 * The words for an English text in the current language, with `{name}` placeholders
 * filled from `vars`. The English text is the key; write it out literally (the unit
 * test reads the keys from the source).
 */
export function tx(english: string, vars?: Record<string, string | number>): string {
  const text = current === 'de' && catalog ? (catalog[english] ?? english) : english;
  return fill(text, vars);
}

/**
 * A count with its noun: `txn(n, '{n} sheet', '{n} sheets')`. `{n}` is the number,
 * written the way the language writes numbers; both forms are catalog keys.
 */
export function txn(n: number, one: string, other: string, vars?: Record<string, string | number>): string {
  return tx(n === 1 ? one : other, { n: fmtNum(n), ...vars });
}

/**
 * A label used inside a sentence: lower-cased in English ("Add to your picks"), as it is
 * in German, where nouns keep their capital ("Zu Deine Auswahl" reads as address).
 */
export const lc = (label: string): string => (current === 'de' ? label : label.toLowerCase());

/* ------------------------------------------------------------------ tables */

/** The German side of a table: the same shape, any words. */
export type Translation<T> = T extends string
  ? string
  : T extends (...args: infer A) => infer R
    ? (...args: A) => Translation<R>
    : T extends object
      ? { readonly [K in keyof T]: Translation<T[K]> }
      : T;

interface Registered {
  name: string;
  en: unknown;
  de: unknown;
}
const registry: Registered[] = [];

/** Every table made with `localized()` so far (for the unit test). */
export const localizedTables = (): readonly Registered[] => registry;

/**
 * A table of words in both languages behind one value: reading a key gives the current
 * language's. `name` is for the unit test's messages.
 */
export function localized<E extends object>(name: string, tables: { en: E; de: Translation<E> }): E {
  registry.push({ name, en: tables.en, de: tables.de });
  const pick = (): object => (current === 'de' ? (tables.de as object) : tables.en);
  return new Proxy(tables.en, {
    get: (_target, prop) => Reflect.get(pick(), prop),
    has: (_target, prop) => Reflect.has(pick(), prop),
    ownKeys: () => Reflect.ownKeys(pick()),
    getOwnPropertyDescriptor: (target, prop) => {
      const desc = Reflect.getOwnPropertyDescriptor(pick(), prop);
      const own = Reflect.getOwnPropertyDescriptor(target, prop);
      // a proxy may not report a non-configurable property (an array's length) differently
      if (desc && own && !own.configurable) return { ...own, value: desc.value };
      return desc ? { ...desc, configurable: true } : undefined;
    },
  });
}

/* ------------------------------------------------------------------ formats */

/** The locale for dates and numbers: German dates for German, British ones for English. */
export const formatLocale = (): string => (current === 'de' ? 'de-DE' : 'en-GB');

/** A number as the language writes it: 1,234 or 1.234. */
export const fmtNum = (n: number): string => n.toLocaleString(formatLocale());

/** A date (a Date, an ISO string or epoch ms) with `toLocaleDateString` options. */
export function fmtDate(d: Date | string | number, options?: Intl.DateTimeFormatOptions): string {
  const date = d instanceof Date ? d : new Date(d);
  return Number.isNaN(date.getTime()) ? String(d) : date.toLocaleDateString(formatLocale(), options);
}

/** A date and time with `toLocaleString` options (default: medium date, short time). */
export function fmtDateTime(d: Date | string | number, options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' }): string {
  const date = d instanceof Date ? d : new Date(d);
  return Number.isNaN(date.getTime()) ? String(d) : date.toLocaleString(formatLocale(), options);
}

/** A time of day with `toLocaleTimeString` options. */
export function fmtTime(d: Date | string | number, options?: Intl.DateTimeFormatOptions): string {
  const date = d instanceof Date ? d : new Date(d);
  return Number.isNaN(date.getTime()) ? String(d) : date.toLocaleTimeString(formatLocale(), options);
}
